import {Card,Button, Col, Flex, Input, Row, Table, Typography } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import styles from "./ReviewInfo.module.scss";
import { getReviewsAction } from "../../../../../../redux/services/reviews/reviewsServices";
import {
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../../../../constants/routeConsts";
import Actions from "../../../../../../redux/actions";
import ReviewInfoDialog from "./ReviewInfoDialog";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import { SearchOutlined,MessageOutlined } from "@ant-design/icons";
import { colors } from "../../../../../../constants/colors";

const ReviewInfo = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { reviews } = useSelector((state) => state.reviews);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: reviews?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );

  /* dialog dependencies */
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue
    }
    dispatch(getReviewsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("reviewInfo.orderId"),
        dataIndex: "orderId",
        render: (_, record) => record?.order?.order_number,
      },
      {
        title: t("reviewInfo.ReviewDate"),
        dataIndex: "appointmentDate",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("reviewInfo.customerName"),
        render: (_, record) =>
          record?.user?.first_name || record?.user?.last_name
            ? `${record?.user?.first_name} ${record?.user?.last_name}`
            : "-",
      },
      {
        title: t("reviewInfo.serviceProviderName"),
        dataIndex: "serviceProivderName",
        render: (_, record) => record?.organization?.name ?? '-',
      },
      {
        title: t("reviewInfo.headline"),
        dataIndex: "headline",
        key: "headline",
      },
      {
        title: t("reviewInfo.rating"),
        dataIndex: "rating",
        key: "rating",
      },
      {
        title: t("reviewInfo.detailedReview"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
      {
        title: t("CommonUtils.leaveAReview"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleReviewDetails(e, record)}>
                Review
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    navigate(baseRouteMakerForSuperAdmin(routes.VIEW_REVIEW_INFO));
    dispatch(
      Actions.createAction(Actions.SELECTED_REVIEW_ID, {
        selectedReviewId: row._id,
      })
    );
  };

  const handleReviewDetails = (e, row) => {
    setOpenReviewDialog(true);
    dispatch(Actions.createAction(Actions.SELECTED_REVIEW_ID, {selectedReviewId: row._id}));
  };

  return (
    <div className={styles.reviewInfoContainer}>
      <Flex vertical gap="2rem">
      <Row gutter={[24]} justify="space-between">
      <Col span={8}>
          <Card
                  bordered
                  hoverable
                  style={{
                    width: 240, padding: 0, borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<MessageOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("reviewInfo.reviews")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${reviews?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
          </Card>
          </Col>
          <Col span={7}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder="Search"
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table dataSource={reviews?.data} columns={columns} pagination = {paginationObj} />
        )}
      </Flex>
      <ReviewInfoDialog
      open={openReviewDialog}
      onClose={()=>{
        setOpenReviewDialog(false)
        handleGetTableData()
      }}
      />
    </div>
  );
};

export default ReviewInfo;
